import { Types } from "types/_utils.types";
import { MemberRole, TaskStatus } from "../../dtos/tasks/template.dto";

export enum TaskEventType {
  /** Status moved, including cascades and automatic activation. */
  STATUS = "status",
  /** A member slot was filled, changed or cleared. */
  MEMBER = "member",
  /** The deadline duration was set, changed or removed on the board copy. */
  DEADLINE = "deadline",
  /** dueAt passed while the task was still open. Recorded once. */
  DEADLINE_PASSED = "deadline_passed",
  /** Name, description or linked stage changed. */
  DETAILS = "details",
  /** The board was brought in line with its template. */
  SYNCED = "synced",
  /** A reminder went out. This is what makes the log the dedupe source. */
  NOTIFIED = "notified",
}

export type NotifyTrigger = "activated" | "overdue" | "unresolved";

export type TaskEventData = {
  [TaskEventType.STATUS]: {
    from: TaskStatus;
    to: TaskStatus;
    outcome?: Types.ObjectId;
    reason?: string;
    /** Set when this task changed because another one did. */
    cascadedFrom?: Types.ObjectId;
    /** Set when the chain advanced rather than a person acting. */
    automatic?: boolean;
  };
  [TaskEventType.MEMBER]: {
    role: MemberRole;
    from?: {
      target: string;
      assigneeId?: Types.ObjectId;
      assigneeRole?: string;
    };
    to?: {
      target: string;
      assigneeId?: Types.ObjectId;
      assigneeRole?: string;
    };
  };
  [TaskEventType.DEADLINE]: {
    from?: { days: number; businessDays?: boolean };
    to?: { days: number; businessDays?: boolean };
    dueAt?: Date;
  };
  [TaskEventType.DEADLINE_PASSED]: { dueAt: Date; lateBy: number };
  [TaskEventType.DETAILS]: { fields: string[] };
  [TaskEventType.SYNCED]: {
    template: Types.ObjectId;
    fields: string[];
    added?: boolean;
  };
  [TaskEventType.NOTIFIED]: {
    trigger: NotifyTrigger;
    recipients: {
      userId?: Types.ObjectId;
      email: string;
      external: boolean;
    }[];
    channels: ("app" | "email" | "sms")[];
  };
};

export type TaskEvent = {
  [T in TaskEventType]: {
    _id: Types.ObjectId;
    type: T;
    at: Date;
    /** Absent means the system did it — a cascade, the scheduler, a template push. */
    by?: Types.ObjectId;
    data: TaskEventData[T];
  };
}[TaskEventType];

export type TaskEventInput<T extends TaskEventType = TaskEventType> = {
  taskId: Types.ObjectId;
  type: T;
  by?: Types.ObjectId;
  at?: Date;
  data: TaskEventData[T];
};
